import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../services/adminApi';
import { organizationAPI } from '../../services/api';
import { useToast } from '../shared/Toast';
import { isKioskToggleLocked, kioskBlockedError } from '../../utils/pricingPhase';
import { LoadFailed } from '../settings/instantSettings';
import { SheetField, SheetSection } from '../settings/sheet';
import {
  Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, KeyValue, ListPage, ListToolbar,
  PRIMARY_BTN, StatusChip, matchesSearch,
} from '../shared/listPage';
import { DRAWER_FORM_CLS, KioskLockedNote, RequiredMark, ToggleRow } from './SuperAdminStoresTab';
import { Loader2, Plus, Search, Trash2 } from 'lucide-react';

/**
 * Super admin → «Οργανισμοί» στη διάταξη «Πίνακας» (14/10/2026): πίνακας οργανισμών, και κλικ στη γραμμή
 * ανοίγει συρτάρι με τα στοιχεία, τα καταστήματα και τους διαχειριστές του (ως τότε η γραμμή «άνοιγε» μέσα
 * στη λίστα). Νέο κατάστημα / νέος διαχειριστής ανοίγουν δεύτερο συρτάρι από πάνω· όταν κλείσει, ο
 * οργανισμός φαίνεται ενημερωμένος. Ίδια endpoints, σώματα, έλεγχοι και μηνύματα με πριν.
 */

interface Org {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: boolean;
  store_count?: number;
  member_count?: number;
}

interface OrgDetail {
  organization: Org;
  stores: any[];
  members: any[];
  my_role: string;
}

// Η PG στέλνει τα COUNT ως κείμενο.
const num = (v: unknown): number => {
  const n = parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};

// Μικρά κομμάτια των λιστών του συρταριού (καταστήματα, διαχειριστές): χωρίς εικονίδια-κουτιά, μόνο tokens.
const REMOVE_BTN = 'w-10 h-10 shrink-0 rounded-xl text-t3 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-500/10 '
  + 'flex items-center justify-center transition-colors disabled:opacity-50';
const COMPACT_SELECT = 'min-h-[40px] pl-3 pr-8 border border-line-strong rounded-xl bg-surface-2 text-t1 text-sm '
  + 'focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring disabled:opacity-50';
const PICKER_LIST = 'mt-2 max-h-60 overflow-y-auto rounded-xl border border-line divide-y divide-line';
const PICKER_ITEM = 'w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left text-sm hover:bg-surface-3 transition-colors';

const ROLE_OPTIONS = ['org_viewer', 'org_admin', 'org_owner'] as const;

const SuperAdminOrgsTab: React.FC = () => {
  const { t } = useTranslation(['superAdminPanel', 'admin', 'listPage', 'common']);
  const { showToast } = useToast();
  // Εδώ μόνο δημιουργούνται καταστήματα: νέο κατάστημα = πάντα κλειδωμένο kiosk ως τη Φ5.
  const newStoreKioskLocked = isKioskToggleLocked('create');
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState<number | null>(null);
  const [orgDetail, setOrgDetail] = useState<OrgDetail | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  // Ποιος οργανισμός είναι ανοιχτός ΤΩΡΑ: μια αργή απάντηση για άλλον δεν γράφει πάνω του.
  const openOrgRef = useRef<number | null>(null);

  // Create org
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', contact_email: '', contact_phone: '' });
  const [creatingOrg, setCreatingOrg] = useState(false);

  // Store picker
  const [allStores, setAllStores] = useState<any[]>([]);
  const [storeSearch, setStoreSearch] = useState('');
  const [showStorePicker, setShowStorePicker] = useState(false);

  // Create store inline
  const [showCreateStore, setShowCreateStore] = useState(false);
  const [creatingStore, setCreatingStore] = useState(false);
  const [newStore, setNewStore] = useState({
    name: '', address: '', phone_number: '', email: '', vat_number: '',
    username: '', password: '', branch_code: '',
    warehouse: true, delivery: true, delivery_self: true, delivery_efood: false, delivery_wolt: false,
    takeaway: true, dine_in: false, kiosk_enabled: false, end_of_day: '03:00'
  });
  const [newManager, setNewManager] = useState({ first_name: '', last_name: '', email: '', phone: '', pin_code: '' });
  const [createForOrgId, setCreateForOrgId] = useState<number | null>(null);

  // Member picker
  const [allOrgUsers, setAllOrgUsers] = useState<any[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [addMemberRole, setAddMemberRole] = useState('org_admin');

  // Create org user
  const [showCreateOrgUser, setShowCreateOrgUser] = useState(false);
  const [creatingOrgUser, setCreatingOrgUser] = useState(false);
  const [newOrgUser, setNewOrgUser] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '' });

  useEffect(() => {
    fetchOrgs();
    fetchAllData();
    // Μία φορά στο άνοιγμα της σελίδας, όπως πριν.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    try {
      const storesData = await adminAPI.getStores();
      setAllStores(storesData as any || []);
      try {
        const orgUsersData = await organizationAPI.listOrgUsers();
        setAllOrgUsers(orgUsersData.users || []);
      } catch { setAllOrgUsers([]); }
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      const data = await organizationAPI.list();
      setOrgs(data.organizations || []);
      setLoadFailed(false);
    } catch (err) {
      console.error('Failed to load orgs:', err);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Κλικ στη γραμμή: το συρτάρι ανοίγει αμέσως και γεμίζει μόλις έρθουν τα στοιχεία (GET /organizations/:id).
  const openOrg = async (orgId: number) => {
    openOrgRef.current = orgId;
    setExpandedOrg(orgId);
    setOrgDetail(null);
    setDetailFailed(false);
    try {
      const data = await organizationAPI.get(orgId);
      if (openOrgRef.current !== orgId) return;
      setOrgDetail(data);
    } catch (err) {
      console.error('Failed to load org detail:', err);
      if (openOrgRef.current === orgId) setDetailFailed(true);
    }
  };

  const closeOrg = () => {
    openOrgRef.current = null;
    setExpandedOrg(null);
    setOrgDetail(null);
    setDetailFailed(false);
    setStoreSearch('');
    setShowStorePicker(false);
    setMemberSearch('');
    setShowMemberPicker(false);
  };

  const handleCreateOrg = async () => {
    if (!newOrg.name || !newOrg.slug) return;
    try {
      setCreatingOrg(true);
      await organizationAPI.create(newOrg);
      setShowCreateOrg(false);
      setNewOrg({ name: '', slug: '', contact_email: '', contact_phone: '' });
      await fetchOrgs();
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToCreate'));
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleDeleteOrg = async (orgId: number) => {
    if (!window.confirm(t('admin:confirmDeleteOrgShort'))) return;
    try {
      await organizationAPI.delete(orgId);
      closeOrg();
      await fetchOrgs();
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToDelete'));
    }
  };

  const handleAddStoreToOrg = async (orgId: number, storeId: number) => {
    try {
      await organizationAPI.addStore(orgId, storeId);
      setStoreSearch('');
      setShowStorePicker(false);
      const data = await organizationAPI.get(orgId);
      setOrgDetail(data);
      await fetchOrgs();
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToAdd'));
    }
  };

  const handleRemoveStoreFromOrg = async (orgId: number, storeId: number) => {
    try {
      await organizationAPI.removeStore(orgId, storeId);
      const data = await organizationAPI.get(orgId);
      setOrgDetail(data);
      await fetchOrgs();
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToRemove'));
    }
  };

  const handleCreateNewStore = async () => {
    if (!createForOrgId) return;
    if (!newStore.name.trim()) { window.alert(t('admin:nameRequiredShort')); return; }
    if (!newStore.username.trim()) { window.alert(t('admin:usernameRequiredShort')); return; }
    if (!newStore.password.trim() || newStore.password.length < 6) { window.alert(t('admin:passwordMinErrorShort')); return; }
    if (!newManager.first_name.trim() || !newManager.last_name.trim()) { window.alert(t('admin:managerNameRequiredShort')); return; }
    if (!newManager.email.trim()) { window.alert(t('admin:managerEmailRequiredShort')); return; }
    if (!newManager.pin_code.trim() || newManager.pin_code.length !== 6) { window.alert(t('admin:managerPinErrorShort')); return; }

    try {
      setCreatingStore(true);
      // Ο διευθυντής ΜΑΖΙ με το κατάστημα σε ένα αίτημα (16/10/2026): μία συναλλαγή στον server, όχι πια
      // κατάστημα χωρίς διαχειριστή όταν το email υπάρχει ήδη.
      const storeResponse: any = await adminAPI.createStore({ ...newStore, manager: newManager });
      const storeId = storeResponse.id;

      setShowCreateStore(false);
      setNewStore({ name: '', address: '', phone_number: '', email: '', vat_number: '', username: '', password: '', branch_code: '', warehouse: true, delivery: true, delivery_self: true, delivery_efood: false, delivery_wolt: false, takeaway: true, dine_in: false, kiosk_enabled: false, end_of_day: '03:00' });
      setNewManager({ first_name: '', last_name: '', email: '', phone: '', pin_code: '' });
      await fetchAllData();
      await handleAddStoreToOrg(createForOrgId, storeId);
      setCreateForOrgId(null);
    } catch (err: any) {
      // 409 της φραγής kiosk → toast με το μήνυμα του server, η φόρμα μένει ανοιχτή.
      const kioskMsg = kioskBlockedError(err);
      if (kioskMsg) showToast('error', kioskMsg, 8000);
      else window.alert(err.response?.data?.error || t('admin:failedToCreate'));
    } finally {
      setCreatingStore(false);
    }
  };

  const handleAddMemberToOrg = async (orgId: number, orgUserId: number) => {
    try {
      await organizationAPI.addMember(orgId, orgUserId, addMemberRole);
      setMemberSearch('');
      setShowMemberPicker(false);
      setAddMemberRole('org_admin');
      const data = await organizationAPI.get(orgId);
      setOrgDetail(data);
      await fetchOrgs();
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToAdd'));
    }
  };

  const handleRemoveMemberFromOrg = async (orgId: number, orgUserId: number) => {
    try {
      await organizationAPI.removeMember(orgId, orgUserId);
      const data = await organizationAPI.get(orgId);
      setOrgDetail(data);
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToRemove'));
    }
  };

  const handleUpdateMemberRole = async (orgId: number, orgUserId: number, role: string) => {
    try {
      await organizationAPI.updateMemberRole(orgId, orgUserId, role);
      const data = await organizationAPI.get(orgId);
      setOrgDetail(data);
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToUpdate'));
    }
  };

  const handleCreateNewOrgUser = async () => {
    if (!createForOrgId) return;
    if (!newOrgUser.first_name.trim() || !newOrgUser.last_name.trim()) { window.alert(t('admin:firstLastNameRequiredShort')); return; }
    if (!newOrgUser.email.trim()) { window.alert(t('admin:emailRequiredShort')); return; }
    if (!newOrgUser.password.trim() || newOrgUser.password.length < 6) { window.alert(t('admin:passwordMinErrorShort')); return; }

    try {
      setCreatingOrgUser(true);
      const result: any = await organizationAPI.createOrgUser(newOrgUser);
      setShowCreateOrgUser(false);
      setNewOrgUser({ first_name: '', last_name: '', email: '', password: '', phone: '' });
      await fetchAllData();
      await handleAddMemberToOrg(createForOrgId, result.user.id);
      setCreateForOrgId(null);
    } catch (err: any) {
      window.alert(err.response?.data?.error || t('admin:failedToCreate'));
    } finally {
      setCreatingOrgUser(false);
    }
  };

  // Αναζήτηση στη μνήμη, χωρίς τόνους και πεζά (όπως οι Υπάλληλοι).
  const getFilteredStores = () => {
    const orgStoreIds = new Set(orgDetail?.stores.map((s: any) => s.id) || []);
    return allStores
      .filter((s: any) => !orgStoreIds.has(s.id))
      .filter((s: any) => matchesSearch(storeSearch, s.name, s.id));
  };

  const getFilteredOrgUsers = () => {
    const orgMemberIds = new Set(orgDetail?.members.map((m: any) => m.org_user_id) || []);
    return allOrgUsers
      .filter((u: any) => !orgMemberIds.has(u.id))
      .filter((u: any) => matchesSearch(memberSearch, u.first_name, u.last_name, u.email));
  };

  const closeCreateStore = () => { setShowCreateStore(false); setCreateForOrgId(null); };
  const closeCreateOrgUser = () => { setShowCreateOrgUser(false); setCreateForOrgId(null); };

  const roleLabel = (role: string) => (role === 'org_viewer'
    ? t('admin:roleViewer')
    : role === 'org_owner' ? t('admin:roleOwner') : t('admin:roleAdmin'));

  const columns = useMemo<Column<Org>[]>(() => [
    {
      key: 'name',
      header: t('superAdminPanel:orgs.colOrg'),
      width: 'minmax(220px, 2fr)',
      sortable: true,
      render: o => (
        <>
          <span className="block font-medium">{o.name}</span>
          <span className="block text-xs text-t3">{o.slug}</span>
        </>
      ),
      stackRender: o => o.name,
    },
    {
      key: 'stores',
      header: t('superAdminPanel:orgs.colStores'),
      width: '140px',
      align: 'right',
      sortable: true,
      sortValue: o => num(o.store_count),
      render: o => <span className="text-t2">{num(o.store_count)}</span>,
      stackRender: o => t('superAdminPanel:orgs.storesCount', { count: num(o.store_count) }),
    },
    {
      key: 'members',
      header: t('superAdminPanel:orgs.colMembers'),
      width: '140px',
      align: 'right',
      hideBelow: 'medium',
      sortable: true,
      sortValue: o => num(o.member_count),
      render: o => <span className="text-t2">{num(o.member_count)}</span>,
    },
    {
      key: 'contact',
      header: t('superAdminPanel:orgs.colContact'),
      width: 'minmax(180px, 1.4fr)',
      hideBelow: 'medium',
      render: o => (
        <>
          <span className="block text-t2">{o.contact_email || '—'}</span>
          <span className="block text-xs text-t3">{o.contact_phone || '—'}</span>
        </>
      ),
    },
    {
      key: 'status',
      header: t('admin:statusLabel'),
      width: '120px',
      stack: 'end',
      render: o => (o.is_active
        ? <StatusChip tone="ok">{t('admin:activeStatus')}</StatusChip>
        : <StatusChip tone="muted">{t('admin:inactiveStatus')}</StatusChip>),
    },
  ], [t]);

  const selectedOrg = orgs.find(o => o.id === expandedOrg) || orgDetail?.organization || null;
  const detailStores = orgDetail ? orgDetail.stores.length : num(selectedOrg?.store_count);
  const detailMembers = orgDetail ? orgDetail.members.length : num(selectedOrg?.member_count);
  const firstLoad = loading && orgs.length === 0;

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.customers')}
      title={t('admin:organizations')}
      subtitle={t('superAdminPanel:orgs.subtitle')}
      testId="sa-orgs"
    >
      <ListToolbar
        actions={(
          <button type="button" className={PRIMARY_BTN} onClick={() => setShowCreateOrg(true)}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('admin:newOrg')}
          </button>
        )}
        summary={firstLoad || (loadFailed && orgs.length === 0) ? null : t('superAdminPanel:orgs.count', { count: orgs.length })}
      />

      {loadFailed && orgs.length === 0 ? (
        <LoadFailed message={t('superAdminPanel:orgs.loadFailed')} onRetry={fetchOrgs} />
      ) : (
        <DataTable
          columns={columns}
          rows={orgs}
          rowKey={o => o.id}
          onRowClick={o => openOrg(o.id)}
          selectedKey={expandedOrg}
          loading={loading}
          stickyHeader
          rowLabel={o => t('superAdminPanel:orgs.rowLabel', { name: o.name, slug: o.slug })}
          empty={<EmptyState title={t('admin:noOrganizations')} hint={t('admin:noOrgsDescriptionShort')} />}
          testId="sa-orgs-table"
        />
      )}

      {/* ─── Ο οργανισμός: στοιχεία, καταστήματα, διαχειριστές ───────── */}
      <DetailPanel
        open={expandedOrg !== null}
        onClose={closeOrg}
        title={selectedOrg?.name || ''}
        subtitle={selectedOrg ? [
          selectedOrg.slug,
          t('superAdminPanel:orgs.storesCount', { count: detailStores }),
          t('superAdminPanel:orgs.membersCount', { count: detailMembers }),
        ].join(' · ') : undefined}
        width="lg"
        testId="sa-org-detail"
        footerStart={expandedOrg !== null && (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => handleDeleteOrg(expandedOrg)}>
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {t('admin:deleteOrgBtn')}
          </button>
        )}
        footer={<button type="button" className={GHOST_BTN} onClick={closeOrg}>{t('listPage:close')}</button>}
      >
        {detailFailed ? (
          <LoadFailed message={t('superAdminPanel:orgs.detailFailed')} onRetry={() => { if (expandedOrg !== null) openOrg(expandedOrg); }} />
        ) : !orgDetail || expandedOrg === null ? (
          <div role="status" className="flex items-center justify-center gap-2 py-16 text-sm text-t3">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            {t('listPage:loading')}
          </div>
        ) : (
          <div className={DRAWER_FORM_CLS}>
            <SheetSection id="sa-org-details" title={t('superAdminPanel:orgs.detailsTitle')}>
              <KeyValue
                testId="sa-org-details"
                items={[
                  { id: 'slug', label: t('admin:slug'), value: orgDetail.organization?.slug ?? selectedOrg?.slug },
                  { id: 'email', label: t('admin:email'), value: orgDetail.organization?.contact_email ?? selectedOrg?.contact_email },
                  { id: 'phone', label: t('admin:phone'), value: orgDetail.organization?.contact_phone ?? selectedOrg?.contact_phone },
                  {
                    id: 'status',
                    label: t('admin:statusLabel'),
                    value: (orgDetail.organization?.is_active ?? selectedOrg?.is_active)
                      ? <StatusChip tone="ok">{t('admin:activeStatus')}</StatusChip>
                      : <StatusChip tone="muted">{t('admin:inactiveStatus')}</StatusChip>,
                  },
                ]}
              />
            </SheetSection>

            {/* Καταστήματα */}
            <SheetSection id="sa-org-stores" title={`${t('admin:stores')} (${orgDetail.stores.length})`} hint={t('superAdminPanel:orgs.storesHint')}>
              {orgDetail.stores.length === 0 ? (
                <p className="text-sm text-t3">{t('admin:noStoreInOrg')}</p>
              ) : (
                <ul className="divide-y divide-line max-h-72 overflow-y-auto" data-testid="sa-org-stores">
                  {orgDetail.stores.map((s: any) => (
                    <li key={s.id} className="flex items-center justify-between gap-3 py-1.5">
                      <span className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className={`text-sm ${s.is_active ? 'text-t1' : 'text-t3'}`}>{s.name}</span>
                        <span className="text-xs text-t4">#{s.id}</span>
                        {!s.is_active && <StatusChip tone="muted">{t('superAdminPanel:orgs.storeInactive')}</StatusChip>}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveStoreFromOrg(expandedOrg, s.id)}
                        className={REMOVE_BTN}
                        title={t('admin:remove')}
                        aria-label={t('superAdminPanel:orgs.removeStore', { name: s.name })}
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {/* Store picker */}
              <div className="mt-4">
                <div className="flex gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-t4" aria-hidden="true" />
                    <input
                      type="text"
                      placeholder={t('admin:searchDots')}
                      aria-label={t('superAdminPanel:orgs.addStoreLabel')}
                      value={storeSearch}
                      onChange={(e) => { setStoreSearch(e.target.value); setShowStorePicker(true); }}
                      onFocus={() => setShowStorePicker(true)}
                      className={`${INPUT_CLS} pl-10`}
                      autoComplete="off"
                    />
                  </div>
                  <button type="button" className={GHOST_BTN} onClick={() => { setCreateForOrgId(expandedOrg); setShowCreateStore(true); }}>
                    <Plus className="w-4 h-4" aria-hidden="true" />{t('admin:newItem')}
                  </button>
                </div>
                {showStorePicker && (
                  <ul className={PICKER_LIST} aria-label={t('superAdminPanel:orgs.addStoreLabel')} data-testid="sa-org-store-picker">
                    {getFilteredStores().length > 0 ? (
                      getFilteredStores().slice(0, 20).map((s: any) => (
                        <li key={s.id}>
                          <button type="button" onClick={() => handleAddStoreToOrg(expandedOrg, s.id)} className={PICKER_ITEM}>
                            <span className="min-w-0 flex items-center gap-2">
                              <span className={`truncate ${s.is_active ? 'text-t1' : 'text-t3'}`}>{s.name}</span>
                              {!s.is_active && <StatusChip tone="muted">{t('superAdminPanel:orgs.storeInactive')}</StatusChip>}
                            </span>
                            <span className="text-xs text-t4 shrink-0">#{s.id}</span>
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="px-3.5 py-3 text-sm text-t3 text-center">{t('admin:noResultsShort')}</li>
                    )}
                  </ul>
                )}
              </div>
            </SheetSection>

            {/* Διαχειριστές */}
            <SheetSection id="sa-org-members" title={`${t('admin:orgAdmins')} (${orgDetail.members.length})`} hint={t('superAdminPanel:orgs.membersHint')}>
              {orgDetail.members.length === 0 ? (
                <p className="text-sm text-t3">{t('admin:noAdminInOrg')}</p>
              ) : (
                <ul className="divide-y divide-line max-h-72 overflow-y-auto" data-testid="sa-org-members">
                  {orgDetail.members.map((m: any) => {
                    const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
                    return (
                      <li key={m.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2">
                        <div className="min-w-0">
                          <p className="text-sm text-t1">{name}</p>
                          <p className="text-xs text-t3 truncate">{m.email}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <select
                            value={m.role}
                            onChange={(e) => handleUpdateMemberRole(expandedOrg, m.org_user_id, e.target.value)}
                            aria-label={t('superAdminPanel:orgs.memberRole', { name })}
                            className={COMPACT_SELECT}
                          >
                            {ROLE_OPTIONS.map(role => <option key={role} value={role}>{roleLabel(role)}</option>)}
                          </select>
                          <button
                            type="button"
                            onClick={() => handleRemoveMemberFromOrg(expandedOrg, m.org_user_id)}
                            className={REMOVE_BTN}
                            title={t('admin:remove')}
                            aria-label={t('superAdminPanel:orgs.removeMember', { name })}
                          >
                            <Trash2 className="w-4 h-4" aria-hidden="true" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {/* Member picker */}
              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-t4" aria-hidden="true" />
                    <input
                      type="text"
                      placeholder={t('admin:searchAdminPlaceholder')}
                      aria-label={t('superAdminPanel:orgs.addMemberLabel')}
                      value={memberSearch}
                      onChange={(e) => { setMemberSearch(e.target.value); setShowMemberPicker(true); }}
                      onFocus={() => setShowMemberPicker(true)}
                      className={`${INPUT_CLS} pl-10`}
                      autoComplete="off"
                    />
                  </div>
                  <select
                    value={addMemberRole}
                    onChange={(e) => setAddMemberRole(e.target.value)}
                    aria-label={t('superAdminPanel:orgs.newMemberRole')}
                    className={`${COMPACT_SELECT} min-h-[44px]`}
                  >
                    {ROLE_OPTIONS.map(role => <option key={role} value={role}>{roleLabel(role)}</option>)}
                  </select>
                  <button type="button" className={GHOST_BTN} onClick={() => { setCreateForOrgId(expandedOrg); setShowCreateOrgUser(true); }}>
                    <Plus className="w-4 h-4" aria-hidden="true" />{t('admin:newMember')}
                  </button>
                </div>
                {showMemberPicker && (
                  <ul className={PICKER_LIST} aria-label={t('superAdminPanel:orgs.addMemberLabel')} data-testid="sa-org-member-picker">
                    {getFilteredOrgUsers().length > 0 ? (
                      getFilteredOrgUsers().slice(0, 20).map((u: any) => (
                        <li key={u.id}>
                          <button type="button" onClick={() => handleAddMemberToOrg(expandedOrg, u.id)} className={PICKER_ITEM}>
                            <span className="min-w-0 truncate text-t1">{u.first_name} {u.last_name}</span>
                            <span className="text-xs text-t3 truncate">{u.email}</span>
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="px-3.5 py-3 text-sm text-t3 text-center">{t('admin:noResultsShort')}</li>
                    )}
                  </ul>
                )}
              </div>
            </SheetSection>
          </div>
        )}
      </DetailPanel>

      {/* ─── Νέος οργανισμός ─────────────────────────────────────────── */}
      <DetailPanel
        open={showCreateOrg}
        onClose={() => setShowCreateOrg(false)}
        title={t('admin:newOrganization')}
        subtitle={t('superAdminPanel:orgs.newOrgHint')}
        testId="sa-org-create"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setShowCreateOrg(false)}>{t('common:cancel')}</button>
            <button type="submit" form="sa-org-create-form" className={PRIMARY_BTN} disabled={creatingOrg || !newOrg.name || !newOrg.slug}>
              {creatingOrg && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {creatingOrg ? t('admin:creating2') : t('admin:create')}
            </button>
          </>
        )}
      >
        <form id="sa-org-create-form" className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleCreateOrg(); }}>
          <SheetField label={<>{t('admin:name')}<RequiredMark /></>} htmlFor="sa-org-name">
            <input
              id="sa-org-name"
              value={newOrg.name}
              onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') })}
              className={INPUT_CLS}
              placeholder="Coffee Island"
              aria-required="true"
            />
          </SheetField>
          <SheetField label={<>{t('admin:slug')}<RequiredMark /></>} htmlFor="sa-org-slug" hint={t('superAdminPanel:orgs.slugHint')}>
            <input id="sa-org-slug" value={newOrg.slug} onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value })} className={INPUT_CLS}
              placeholder="coffee-island" aria-required="true" aria-describedby="sa-org-slug-hint" />
          </SheetField>
          <div className="xs-grid2">
            <SheetField label={t('admin:email')} htmlFor="sa-org-email">
              <input id="sa-org-email" value={newOrg.contact_email} onChange={(e) => setNewOrg({ ...newOrg, contact_email: e.target.value })} className={INPUT_CLS} />
            </SheetField>
            <SheetField label={t('admin:phone')} htmlFor="sa-org-phone">
              <input id="sa-org-phone" value={newOrg.contact_phone} onChange={(e) => setNewOrg({ ...newOrg, contact_phone: e.target.value })} className={INPUT_CLS} />
            </SheetField>
          </div>
        </form>
      </DetailPanel>

      {/* ─── Νέο κατάστημα στον οργανισμό (γρήγορο) ─────────────────── */}
      <DetailPanel
        open={showCreateStore}
        onClose={closeCreateStore}
        title={t('admin:createNewStore')}
        subtitle={t('admin:autoAddToOrg')}
        width="lg"
        testId="sa-org-new-store"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeCreateStore} disabled={creatingStore}>{t('common:cancel')}</button>
            <button type="submit" form="sa-org-new-store-form" className={PRIMARY_BTN} disabled={creatingStore}>
              {creatingStore
                ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('admin:creating3')}</>
                : <><Plus className="w-4 h-4" aria-hidden="true" />{t('admin:createAndAddBtn')}</>}
            </button>
          </>
        )}
      >
        <form id="sa-org-new-store-form" className={DRAWER_FORM_CLS} onSubmit={(e) => { e.preventDefault(); handleCreateNewStore(); }}>
          <SheetSection id="sa-org-new-store-info" title={t('admin:storeSection')}>
            <div className="space-y-4">
              <SheetField label={<>{t('admin:name')}<RequiredMark /></>} htmlFor="sa-org-store-name">
                <input id="sa-org-store-name" value={newStore.name} onChange={(e) => setNewStore({ ...newStore, name: e.target.value })} className={INPUT_CLS} aria-required="true" />
              </SheetField>
              <SheetField label={t('admin:address')} htmlFor="sa-org-store-address">
                <input id="sa-org-store-address" value={newStore.address} onChange={(e) => setNewStore({ ...newStore, address: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <div className="xs-grid2">
                <SheetField label={t('admin:phone')} htmlFor="sa-org-store-phone">
                  <input id="sa-org-store-phone" value={newStore.phone_number} onChange={(e) => setNewStore({ ...newStore, phone_number: e.target.value })} className={INPUT_CLS} />
                </SheetField>
                <SheetField label={t('admin:email')} htmlFor="sa-org-store-email">
                  <input id="sa-org-store-email" value={newStore.email} onChange={(e) => setNewStore({ ...newStore, email: e.target.value })} className={INPUT_CLS} />
                </SheetField>
                <SheetField label={t('admin:vatNumberShort')} htmlFor="sa-org-store-vat">
                  <input id="sa-org-store-vat" value={newStore.vat_number} onChange={(e) => setNewStore({ ...newStore, vat_number: e.target.value })} className={INPUT_CLS} />
                </SheetField>
                <SheetField label={t('admin:branchCodeLabel')} htmlFor="sa-org-store-branch">
                  <input id="sa-org-store-branch" value={newStore.branch_code} onChange={(e) => setNewStore({ ...newStore, branch_code: e.target.value })} className={INPUT_CLS} />
                </SheetField>
              </div>
            </div>
          </SheetSection>

          <SheetSection id="sa-org-new-store-login" title={t('admin:loginCredentials')} hint={t('superAdminPanel:stores.loginHint')}>
            <div className="xs-grid2">
              <SheetField label={<>{t('superAdminPanel:stores.username')}<RequiredMark /></>} htmlFor="sa-org-store-username">
                <input id="sa-org-store-username" value={newStore.username} onChange={(e) => setNewStore({ ...newStore, username: e.target.value })} className={INPUT_CLS} autoComplete="one-time-code" aria-required="true" />
              </SheetField>
              <SheetField label={<>{t('admin:password')}<RequiredMark /></>} htmlFor="sa-org-store-password">
                <input id="sa-org-store-password" type="password" value={newStore.password} onChange={(e) => setNewStore({ ...newStore, password: e.target.value })} className={INPUT_CLS} autoComplete="one-time-code" aria-required="true" />
              </SheetField>
            </div>
          </SheetSection>

          <SheetSection id="sa-org-new-store-manager" title={t('admin:storeManager')} hint={t('superAdminPanel:stores.managerHint')}>
            <div className="xs-grid2">
              <SheetField label={<>{t('admin:firstName')}<RequiredMark /></>} htmlFor="sa-org-manager-first">
                <input id="sa-org-manager-first" value={newManager.first_name} onChange={(e) => setNewManager({ ...newManager, first_name: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <SheetField label={<>{t('admin:lastName')}<RequiredMark /></>} htmlFor="sa-org-manager-last">
                <input id="sa-org-manager-last" value={newManager.last_name} onChange={(e) => setNewManager({ ...newManager, last_name: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <SheetField label={<>{t('admin:email')}<RequiredMark /></>} htmlFor="sa-org-manager-email">
                <input id="sa-org-manager-email" type="email" value={newManager.email} onChange={(e) => setNewManager({ ...newManager, email: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <SheetField label={t('admin:phone')} htmlFor="sa-org-manager-phone">
                <input id="sa-org-manager-phone" value={newManager.phone} onChange={(e) => setNewManager({ ...newManager, phone: e.target.value })} className={INPUT_CLS} />
              </SheetField>
            </div>
            <SheetField label={t('admin:pinDigitsLabel')} htmlFor="sa-org-manager-pin" className="mt-4">
              <input
                id="sa-org-manager-pin"
                inputMode="numeric"
                maxLength={6}
                value={newManager.pin_code}
                onChange={(e) => setNewManager({ ...newManager, pin_code: e.target.value.replace(/\D/g, '') })}
                className={`${INPUT_CLS} max-w-[12rem]`}
                placeholder="123456"
              />
            </SheetField>
          </SheetSection>

          <SheetSection id="sa-org-new-store-features" title={t('superAdminPanel:stores.featuresTitle')} hint={t('superAdminPanel:stores.featuresHint')}>
            <ToggleRow title={t('admin:warehouse')} checked={newStore.warehouse} onChange={(v) => setNewStore({ ...newStore, warehouse: v })} />
            <ToggleRow title={t('admin:delivery')} subtitle={t('superAdminPanel:stores.deliveryHint')} checked={newStore.delivery} onChange={(v) => setNewStore({ ...newStore, delivery: v })} />
            {newStore.delivery && (
              <>
                <ToggleRow indent title={t('admin:ownDelivery')} checked={newStore.delivery_self} onChange={(v) => setNewStore({ ...newStore, delivery_self: v })} />
                <ToggleRow indent title="e-food" checked={newStore.delivery_efood} onChange={(v) => setNewStore({ ...newStore, delivery_efood: v })} />
                <ToggleRow indent title="Wolt" checked={newStore.delivery_wolt} onChange={(v) => setNewStore({ ...newStore, delivery_wolt: v })} />
              </>
            )}
            <ToggleRow title={t('superAdminPanel:stores.takeaway')} checked={newStore.takeaway} onChange={(v) => setNewStore({ ...newStore, takeaway: v })} />
            <ToggleRow title={t('superAdminPanel:stores.dineIn')} checked={newStore.dine_in} onChange={(v) => setNewStore({ ...newStore, dine_in: v })} />
            <ToggleRow
              testId="org-new-store-kiosk-toggle"
              title={t('superAdminPanel:stores.kiosk')}
              note={newStoreKioskLocked ? <KioskLockedNote /> : undefined}
              checked={!!newStore.kiosk_enabled}
              disabled={newStoreKioskLocked}
              onChange={(v) => { if (!newStoreKioskLocked) setNewStore({ ...newStore, kiosk_enabled: v }); }}
            />
            <SheetField label={t('admin:endOfDay')} htmlFor="sa-org-store-eod" className="mt-6 max-w-[12rem]">
              <input id="sa-org-store-eod" type="time" value={newStore.end_of_day} onChange={(e) => setNewStore({ ...newStore, end_of_day: e.target.value })} className={INPUT_CLS} />
            </SheetField>
          </SheetSection>
        </form>
      </DetailPanel>

      {/* ─── Νέος διαχειριστής οργανισμού ───────────────────────────── */}
      <DetailPanel
        open={showCreateOrgUser}
        onClose={closeCreateOrgUser}
        title={t('admin:newOrgAdmin')}
        subtitle={t('admin:orgAdminLoginDesc')}
        testId="sa-org-new-user"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeCreateOrgUser} disabled={creatingOrgUser}>{t('common:cancel')}</button>
            <button type="submit" form="sa-org-new-user-form" className={PRIMARY_BTN} disabled={creatingOrgUser}>
              {creatingOrgUser
                ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('admin:creating3')}</>
                : <><Plus className="w-4 h-4" aria-hidden="true" />{t('admin:createAndAddBtn')}</>}
            </button>
          </>
        )}
      >
        <form id="sa-org-new-user-form" className={DRAWER_FORM_CLS} onSubmit={(e) => { e.preventDefault(); handleCreateNewOrgUser(); }}>
          <SheetSection id="sa-org-new-user-details" title={t('admin:detailsSection')}>
            <div className="xs-grid2">
              <SheetField label={<>{t('admin:firstName')}<RequiredMark /></>} htmlFor="sa-org-user-first">
                <input id="sa-org-user-first" value={newOrgUser.first_name} onChange={(e) => setNewOrgUser({ ...newOrgUser, first_name: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <SheetField label={<>{t('admin:lastName')}<RequiredMark /></>} htmlFor="sa-org-user-last">
                <input id="sa-org-user-last" value={newOrgUser.last_name} onChange={(e) => setNewOrgUser({ ...newOrgUser, last_name: e.target.value })} className={INPUT_CLS} />
              </SheetField>
            </div>
            <SheetField label={t('admin:phone')} htmlFor="sa-org-user-phone" className="mt-4">
              <input id="sa-org-user-phone" value={newOrgUser.phone} onChange={(e) => setNewOrgUser({ ...newOrgUser, phone: e.target.value })} className={INPUT_CLS} />
            </SheetField>
          </SheetSection>
          <SheetSection id="sa-org-new-user-login" title={t('admin:connectionSection')} hint={t('superAdminPanel:orgs.orgUserHint')}>
            <div className="space-y-4">
              <SheetField label={<>{t('admin:email')}<RequiredMark /></>} htmlFor="sa-org-user-email">
                <input id="sa-org-user-email" type="email" value={newOrgUser.email} onChange={(e) => setNewOrgUser({ ...newOrgUser, email: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <SheetField label={t('admin:passwordMinLabel')} htmlFor="sa-org-user-password">
                <input id="sa-org-user-password" type="password" value={newOrgUser.password} onChange={(e) => setNewOrgUser({ ...newOrgUser, password: e.target.value })} className={INPUT_CLS} autoComplete="one-time-code" />
              </SheetField>
            </div>
          </SheetSection>
        </form>
      </DetailPanel>
    </ListPage>
  );
};

export default SuperAdminOrgsTab;
